import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import "./index.css";
import { pdfExt, videoExt, validExtensions, extOf } from "./utils/fileTypes";
import { listAllFolders, listFiles, moveFile, viewUrl, fetchBlobUrl } from "./api";
import notify from "./utils/notify";

function ImageViewer() {
  /* ───────── Routing & auth ───────── */
  const { "*": folderParam = "" } = useParams();
  const [searchParams] = useSearchParams();
  const requestedFile = searchParams.get("file");

  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  /* ───────── State ───────── */
  const [viewFiles, setViewFiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentFileURL, setCurrentFileURL] = useState("");
  const [currentFileType, setCurrentFileType] = useState("");
  const [allFolders, setAllFolders] = useState([]);
  const urlCache = useRef(new Map());

  /* ───────── Load folder list once ───────── */
  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    listAllFolders(token)
      .then(setAllFolders)
      .catch(err => { console.error(err); navigate("/login"); });
  }, [token, navigate]);

  /* ───────── When folder path changes ───────── */
  useEffect(() => {
    if (!token) { navigate("/login"); return; }

    listFiles(token, folderParam)
      .then(({ files }) => {
        const filtered = files.filter(f => !f.isDirectory && validExtensions.includes(extOf(f.name)));
        setViewFiles(filtered);
        if (requestedFile) {
          const idx = filtered.findIndex(f => f.name === requestedFile);
          setCurrentIndex(idx >= 0 ? idx : 0);
        } else {
          setCurrentIndex(0);
        }
      })
      .catch(err => { console.error(err); navigate("/login"); });
  }, [folderParam, requestedFile, token, navigate]);

/* ───────── Fetch current file ───────── */
useEffect(() => {
  if (!viewFiles.length) {
    setCurrentFileURL("");
    setCurrentFileType("");
    return;
  }

  const fileName = viewFiles[currentIndex].name;
  const ext = extOf(fileName);
  const filePath = folderParam ? `${folderParam}/${fileName}` : fileName;

  if (videoExt.includes(ext)) {
    // For video, use direct URL (with token in the query string) so the browser can stream chunks
    setCurrentFileURL(viewUrl(filePath, token));
    setCurrentFileType("video");
    return;
  }

  // Cache hit for image or PDF
  if (urlCache.current.has(fileName)) {
    setCurrentFileURL(urlCache.current.get(fileName));
    setCurrentFileType(pdfExt.includes(ext) ? "pdf" : "image");
    return;
  }

  // Fetch image or PDF as blob
  fetchBlobUrl(token, filePath)
    .then(url => {
      const type = pdfExt.includes(ext) ? "pdf" : "image";
      setCurrentFileURL(url);
      setCurrentFileType(type);
      urlCache.current.set(fileName, url);
    })
    .catch(console.error);

  return () => setCurrentFileURL(prev => {
    // Revoke only if it's an Object URL (images/pdfs)
    if (prev && !videoExt.includes(ext)) URL.revokeObjectURL(prev);
    return "";
  });
}, [viewFiles, currentIndex, folderParam, token]);


  /* ───────── Prefetch neighbours ───────── */
  useEffect(() => {
    if (!viewFiles.length) return;
    const preload = (delta) => {
      const i = (currentIndex + delta + viewFiles.length) % viewFiles.length;
      const name = viewFiles[i].name;
      if (urlCache.current.has(name) || videoExt.includes(extOf(name))) return;
      const path = folderParam ? `${folderParam}/${name}` : name;
      fetchBlobUrl(token, path)
        .then(url => urlCache.current.set(name, url))
        .catch(() => {});
    };
    preload(1);
    preload(-1);
  }, [currentIndex, viewFiles, folderParam, token]);

  /* ───────── Keyboard nav ───────── */
  const handleNext = useCallback(() => setCurrentIndex(i => (i + 1) % viewFiles.length), [viewFiles.length]);
  const handlePrev = useCallback(() => setCurrentIndex(i => (i - 1 + viewFiles.length) % viewFiles.length), [viewFiles.length]);

  useEffect(() => {
    const key = (e) => {
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "Escape") navigate("/");
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [handleNext, handlePrev, navigate]);

  /* ───────── Move file ───────── */
  const handleMoveCurrent = async (dest) => {
    if (!dest) return;
    const fileName = viewFiles[currentIndex].name;
    try {
      await moveFile(token, fileName, folderParam, dest);
    } catch {
      notify.error("Move failed");
      return;
    }
    notify.success("Moved!");
    const newList = viewFiles.filter((_, i) => i !== currentIndex);
    setViewFiles(newList);
    if (!newList.length) navigate("/");
    else setCurrentIndex(i => Math.min(i, newList.length - 1));
  };

  /* ───────── Early no‑content state ───────── */
  if (!viewFiles.length) {
    return (
      <div className="main-container">
        <div className="empty-state">
          <span className="empty-icon">🖼️</span>
          <p>No photos or videos in this folder</p>
        </div>
        <Link to="/" className="fm-btn fm-btn-ghost">← Back to files</Link>
      </div>
    );
  }

  const currentFileName = viewFiles[currentIndex].name;

  /* ───────── JSX ───────── */
  return (
    <div className="main-container-img">
      <header className="viewer-header">
        <Link to="/" className="fm-btn fm-btn-ghost">← Back</Link>
        <div className="viewer-title">
          <h2 title={currentFileName}>{currentFileName}</h2>
          <span className="viewer-counter">
            {currentIndex + 1} of {viewFiles.length} · {folderParam || "Home"}
          </span>
        </div>
      </header>

      <div className="viewer-frame">
        {viewFiles.length > 1 && (
          <button className="nav-arrow prev" onClick={handlePrev} aria-label="Previous">‹</button>
        )}
        {!currentFileURL && <div className="loader-overlay"><div className="spinner" /></div>}
        {currentFileType === "pdf" && <embed src={currentFileURL} type="application/pdf" className="doc-preview" />}
        {currentFileType === "image" && <img src={currentFileURL} alt={currentFileName} />}
        {currentFileType === "video" && <video src={currentFileURL} controls className="viewer-video" />}
        {viewFiles.length > 1 && (
          <button className="nav-arrow next" onClick={handleNext} aria-label="Next">›</button>
        )}
      </div>

      <div className="viewer-toolbar">
        <a className="fm-btn" href={currentFileURL} download={currentFileName}>⬇️ Download</a>
        <select defaultValue="" onChange={(e) => handleMoveCurrent(e.target.value)}>
          <option value="">Move to folder…</option>
          {allFolders.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
      </div>
    </div>
  );
}

export default ImageViewer;
